/**
 * CancelReservationDialog — the irreversible-cancel confirm (#314), extracted
 * from the Bookings page by Ted #32c / epic E3.1 (#1158) so the Bookings list,
 * the Front Desk's Expected list and the dashboard's Arrivals all ask the same
 * question in the same words.
 *
 * Ted (2026-10-06), on Gingr's "Expected Today": the X next to a dog cancels
 * the reservation. A single misclick used to permanently cancel a real booking
 * (and silently cascade to a linked combo partner), so cancelling asks once.
 *
 * Focus lands on "Keep reservation": an accidental Enter keeps the booking
 * rather than confirming the irreversible cancel. Escape keeps it too.
 *
 * Cancelling a reservation that has not been checked out charges nothing —
 * there is no invoice yet — and the dialog says so, because "will I be billed
 * for this?" is the first thing a desk asks.
 */
import { useEffect, useRef } from 'react';

export interface CancelReservationDialogProps {
  /** What is being cancelled, in words the desk recognises — dogs and owner, never an id. */
  readonly label: string;
  /** The booking is one half of a boarding+grooming combo — its partner goes too. */
  readonly hasComboPartner?: boolean;
  /** The cancel is in flight — both buttons wait. */
  readonly busy?: boolean;
  readonly onKeep: () => void;
  readonly onConfirm: () => void;
}

export function CancelReservationDialog({
  label,
  hasComboPartner = false,
  busy = false,
  onKeep,
  onConfirm,
}: CancelReservationDialogProps): JSX.Element {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Confirm booking cancellation"
      data-testid="cancel-confirm-dialog"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onKeep();
      }}
    >
      <div className="ferro-blocking-dialog__panel">
        <h2>Cancel this booking?</h2>
        <p>Cancelling {label} can’t be undone.</p>
        {hasComboPartner ? (
          <p data-testid="cancel-confirm-combo-warning">
            <strong>This booking is part of a combo</strong> — its linked booking will be
            cancelled too.
          </p>
        ) : null}
        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            ref={keepRef}
            disabled={busy}
            onClick={onKeep}
            data-testid="cancel-confirm-dismiss"
          >
            Keep booking
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--danger"
            disabled={busy}
            onClick={onConfirm}
            data-testid="cancel-confirm-accept"
          >
            Cancel booking
          </button>
        </div>
      </div>
    </div>
  );
}
